/**
 * CommentMode Component
 *
 * Provides a visual overlay when in comment mode, allowing users to select
 * elements on the page to comment on.
 *
 * **Architecture:**
 * - Hook (useElementSelection) handles selection effects:
 *   - Document event listeners (click, mouseover, mouseout)
 *   - Element highlighting (data-multidev-highlighted attribute)
 *   - Cursor changes
 * - ESC key handling is centralized in MultidevCollaborationApp
 *
 * Features:
 * - Full-screen overlay with crosshair cursor
 * - Element highlighting on hover (managed by hook)
 * - Click to select element (managed by hook)
 * - Prevents default click behavior during selection (managed by hook)
 */

import React from 'react';
import { useElementSelection } from '../../hooks/useElementSelection';
import { ElementFingerprint } from '../../lib/types';
import { tokens } from '../../styles/tokens';

export interface CommentModeProps {
  isActive: boolean;
  isSidebarOpen: boolean;
  onElementSelected: (element: HTMLElement, fingerprint: ElementFingerprint, clickPosition: { x: number; y: number }) => void;
}

export const CommentMode: React.FC<CommentModeProps> = ({
  isActive,
  isSidebarOpen,
  onElementSelected,
}) => {
  // Hook manages all element selection effects internally
  // We just pass isActive to control it declaratively
  useElementSelection({
    isActive,
    onElementSelected: (data) => {
      onElementSelected(data.element as HTMLElement, data.fingerprint, data.clickPosition);
    }
  });

  // ESC key handling is centralized in MultidevCollaborationApp

  if (!isActive) {
    return null;
  }

  return (
    <div
      data-testid="comment-mode-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Comment selection mode"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        zIndex: 999998,
        cursor: 'crosshair',
        pointerEvents: 'none' // Allow clicks to pass through to elements beneath
      }}
    >
      <div
        style={{
          position: 'fixed',
          top: tokens.spacing.l,
          // Center in effective viewport: offset by half sidebar width when open
          left: isSidebarOpen ? `calc(50% - calc(${tokens.layout.sidebarWidth} / 2))` : '50%',
          transform: 'translateX(-50%)',
          backgroundColor: tokens.colors.bgDefault,
          padding: `${tokens.spacing.s} ${tokens.spacing.xl}`,
          borderRadius: tokens.borders.radiusContainer,
          boxShadow: tokens.elevation.overlay,
          fontSize: '14px',
          fontWeight: tokens.typography.fontWeight.medium,
          color: tokens.colors.textDefault,
          pointerEvents: 'auto',
          // Animate position during sidebar transition
          transition: `left ${tokens.animation.durationMs}ms ${tokens.animation.timingRaw}`,
        }}
      >
        Click any element to add a comment • Press ESC to cancel
      </div>
    </div>
  );
};
